import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { driverProfileOf } from '@/lib/driverProfile';

import {
    contractSetupKey,
    contractSetupStatusKey,
    contractStatusForSigningKey,
    fetchContractSetup,
    fetchContractSetupStatus,
    fetchContractStatusForSigning,
    fetchPreparationStatus,
    prepStatusKey,
    type ContractSetupStatus,
    type PreparationItem,
} from './data/contract';
import { contractDepositsKey, useContractDeposits } from './data/deposits';
import { contractInsuranceKey, useContractInsurance } from './data/insurance';
import { contractInstalmentKey, fetchContractInstalment } from './data/financials';

/** Re-exported so the step components keep importing it from here. */
export type { PreparationItem };

/**
 * Every read the contract setup page makes.
 *
 * READS ONLY — IT NO LONGER DECIDES ANYTHING. The completion rules this hook inherited from the
 * sheet the page replaced were a second, weaker copy of the readiness question, and the manifest
 * now answers it once: rows come off the snapshot in `setupState`, and whether the contract may be
 * activated comes off `get_contract_setup_status`. The derived flags kept here after that move had
 * no consumers left, so a change to them (the [D-B14] insurance-loan requirement among them) was a
 * change to nothing — which is exactly how a dead rule gets mistaken for an enforced one.
 *
 * The query FUNCTIONS live in `data/*`, one per key. This hook no longer defines a second fetcher
 * for a key another surface already owns — which is what made the insurance read return whichever
 * of two shapes mounted first, and the insurance loan insert fail.
 */
export function useContractSetup(contractId: number) {
    const queryClient = useQueryClient();

    const contractQuery = useQuery({
        queryKey: contractSetupKey(contractId),
        queryFn: () => fetchContractSetup(contractId),
        enabled: !!contractId,
        staleTime: 0,
        refetchOnMount: 'always',
    });
    const { data: contract, isLoading: isLoadingContract, refetch: refetchContract } = contractQuery;

    const prepQuery = useQuery({
        queryKey: prepStatusKey(contractId),
        queryFn: () => fetchPreparationStatus(contractId),
        enabled: !!contractId,
        staleTime: 0,
        refetchOnMount: 'always',
    });
    const { data: prepStatus, isLoading: isLoadingPrep, refetch: refetchPrep } = prepQuery;

    const vehicleId = contract?.vehicles?.id || contract?.vehicle_id;

    // The shared read — same envelope the insurance tab holds, same cache entry.
    const insuranceQuery = useContractInsurance(contractId);
    const { data: insurance } = insuranceQuery;

    // Same key and shape as FinancialsTab, so the two share one cache entry.
    const instalmentQuery = useQuery({
        queryKey: contractInstalmentKey(contractId),
        queryFn: () => fetchContractInstalment(contractId),
        enabled: !!contractId,
    });
    const { data: instalment } = instalmentQuery;

    // The one deposits read — the same rows the signing board derives its DEPOSIT slots from.
    const depositsQuery = useContractDeposits(contractId);
    const { data: depositPayments } = depositsQuery;

    const statusQuery = useQuery({
        queryKey: contractStatusForSigningKey(contractId),
        queryFn: () => fetchContractStatusForSigning(contractId),
        enabled: !!contractId,
    });
    const { data: contractStatus, refetch: refetchContractStatus } = statusQuery;

    /**
     * `get_contract_setup_status` — the SERVER's readiness answer, and the authority the gate rows
     * are keyed to.
     *
     * `staleTime: 0` + `refetchOnMount: 'always'` for the same reason the contract and preparation
     * reads carry them: this is the number the Activate button is drawn from, and a cached one is
     * how the screen and the server come apart. Every mutation that moves a readiness input
     * invalidates this key.
     */
    const setupStatusQuery = useQuery({
        queryKey: contractSetupStatusKey(contractId),
        queryFn: () => fetchContractSetupStatus(contractId),
        enabled: !!contractId,
        staleTime: 0,
        refetchOnMount: 'always',
        // NO BACKOFF ON A SERVER THAT PREDATES THE RPC: three retries would spend ~7s reaching the
        // null-payload fallback and file a Sentry event per page load, for an answer that will not
        // change. The fallback path below is already correct, so failing fast IS the behaviour.
        retry: false,
    });
    const { data: setupStatus } = setupStatusQuery;

    /**
     * EVERY READ HAS ANSWERED — not just the two `isLoading` covers.
     *
     * The landing redirect fires on this and nothing else. `isLoading` on the two lead queries
     * says nothing about the deposits, the insurance, the instalment or the status, so a page
     * that redirected on it resolved "where does this admin need to be" from a snapshot that was
     * still half empty, and landed them somewhere arbitrary.
     *
     * EVERY READ THE LANDING ACTUALLY CONSUMES, WHICH IS NOT THE SAME AS EVERY READ THE PAGE MAKES.
     * The `agreements-completion` read used to be waited on here; it is gone, because no rule left
     * on this page consulted it — the agreement rows reach the manifest through
     * `useContractAgreements`, and their gate state through `get_contract_setup_status`. Waiting on
     * a read the landing does not consult is a round trip of delay bought for nothing.
     */
    const stateReady =
        !!contractId &&
        [
            contractQuery,
            prepQuery,
            insuranceQuery,
            instalmentQuery,
            depositsQuery,
            statusQuery,
            // THE GATE READ JOINS THE LANDING CONDITION. Without it the page could redirect off a
            // snapshot whose gate rows had no server answer yet, and land the admin by the
            // client's fallback derivation rather than by the server's.
            setupStatusQuery,
        ].every((query) => query.isFetched);

    const getItemData = useCallback(
        (itemName: string): PreparationItem | undefined => prepStatus?.find((p) => p.item === itemName),
        [prepStatus],
    );

    const refetchAll = useCallback(() => {
        refetchPrep();
        refetchContract();
        queryClient.invalidateQueries({ queryKey: contractInsuranceKey(contractId) });
        queryClient.invalidateQueries({ queryKey: contractDepositsKey(contractId) });
        queryClient.invalidateQueries({ queryKey: contractInstalmentKey(contractId) });
        queryClient.invalidateQueries({ queryKey: contractSetupStatusKey(contractId) });
    }, [refetchPrep, refetchContract, queryClient, contractId]);

    // ── Shapes ───────────────────────────────────────────────────────────────

    // PostgREST returns profiles as an array (profiles.driver_id FK isn't detected as 1:1) — AND
    // THE ARRAY CAN HOLD MORE THAN THE DRIVER'S OWN ROW. `driver_id` is unique only where
    // `admin_id IS NULL`, so an admin testing against this driver appears here too; `[0]` picked
    // whichever the server happened to return first.
    const driverProfile = driverProfileOf(contract?.drivers?.profiles);

    // The policy half of the insurance envelope. `insurance` itself is returned beside it, because
    // the loans hang from the junction row rather than from the policy.
    const policy = insurance?.policy;

    const canSign = contractStatus === 'DRAFT';

    return {
        contract,
        prepStatus,
        /** The POLICY, which is the half the review board reads. `insurance` is the envelope. */
        insuranceData: policy ?? null,
        insurance,
        instalment,
        depositPayments,
        contractStatus,
        /** The server's readiness payload. Null until the read answers. */
        setupStatus: (setupStatus ?? null) as ContractSetupStatus | null,
        driverProfile,
        vehicleId,
        canSign,
        stateReady,
        isLoading: isLoadingContract || isLoadingPrep,
        isLoadingPrep,
        getItemData,
        refetchAll,
        refetchContractStatus,
    };
}
